import { useCallback, useState } from 'react';
import { ArrowUturnLeftIcon, LockClosedIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import { useFoldResource } from '../hooks/useFoldResource';
import { flockBotSpendAPI, runtimeLimitsAPI } from '../services/api';
import { Modal, PageHeader, PageState, StaleNotice } from './founder-dashboard/onboarding/outreach/OutreachShared';
import { focusRing, formatDateTime } from './founder-dashboard/onboarding/outreach/outreachUtils';
import {
  buildUpdateBody,
  formatCount,
  isPinned,
  outcomeHolds,
  readRequestError,
  sourceLabel,
} from './runtimeLimitsForm';
import { barWidthPercent, formatDollars, formatLongDate } from './runtimeLimitsUsage';
import {
  capLimits,
  capRangeMessage,
  chartRows,
  dollarsInputText,
  formatMicroDollars,
  formatMonth,
  formatPercent,
  microToDollars,
  monthOptions,
  planCapChange,
  sharePercent,
  summarize,
} from './flockBotSpend';

const REASON_MAX_LENGTH = 500;

const SOURCE_BADGE_CLASSES = {
  fold: 'border-blue-200 bg-blue-50 text-blue-800',
  railway_variable: 'border-purple-200 bg-purple-50 text-purple-800',
  default: 'border-gray-200 bg-gray-100 text-gray-700',
};

const LEVEL_TILE_CLASSES = {
  ok: 'border-gray-200 bg-white',
  soft: 'border-amber-300 bg-amber-50',
  hard: 'border-red-300 bg-red-50',
};

const LEVEL_BAR_CLASSES = {
  ok: 'bg-blue-600',
  soft: 'bg-amber-500',
  hard: 'bg-red-600',
};

const AXIS_TICK = { fontSize: 11, fill: '#6b7280' };
const TOOLTIP_STYLE = { backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px' };
const SOFT_COLOR = '#d97706';
const HARD_COLOR = '#dc2626';

const requestLimits = () => runtimeLimitsAPI.list().then((response) => response.data);

function describeCap(limit) {
  return `${formatMicroDollars(limit.value)} (${sourceLabel(limit.source)})`;
}

function describeChanger(limit) {
  const who = limit.updatedBy?.name || limit.updatedBy?.email;
  const when = limit.updatedAt ? formatDateTime(limit.updatedAt) : null;
  if (who && when) return `Set in The Fold by ${who} on ${when}.`;
  if (when) return `Set in The Fold on ${when}.`;
  return 'Set in The Fold.';
}


export default function FlockBotSpendPage() {
  const { isOwner } = useAuth();
  if (!isOwner) return <OwnerRequired />;
  return <SpendBoard />;
}

function OwnerRequired() {
  return (
    <div className="space-y-5">
      <PageHeader title="Flock Bot spend" />
      <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-6 text-amber-900">
        <LockClosedIcon className="mt-0.5 h-6 w-6 flex-none" />
        <p>Fold owner access is required to view Flock Bot spend and change its caps.</p>
      </div>
    </div>
  );
}

function LoadFailed({ title, message, onRetry }) {
  return (
    <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-900">
      <p className="font-semibold">{title}</p>
      <p className="mt-1 text-sm">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className={`mt-4 min-h-[44px] rounded-lg border border-current px-4 py-2 text-sm font-semibold ${focusRing}`}
      >
        Try again
      </button>
    </div>
  );
}

function RefreshFailed({ what, message }) {
  return (
    <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
      {what} could not be refreshed: {message} Showing the last loaded values.
    </div>
  );
}

function SpendBoard() {
  const [month, setMonth] = useState(null);
  const requestReport = useCallback(() => flockBotSpendAPI.get(month).then((response) => response.data.data), [month]);
  const [report, loadReport] = useFoldResource(requestReport, 'Flock Bot spend could not be loaded.');
  const [list, loadLimits] = useFoldResource(requestLimits, 'Caps could not be loaded.');
  const [notice, setNotice] = useState(null);

  if (report.status === 'denied' || list.status === 'denied') return <OwnerRequired />;

  const data = report.status === 'ready' ? report.payload : null;
  const summary = data ? summarize(data) : null;
  const otherMonthShown = Boolean(data && month && data.month !== month);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Flock Bot spend"
        description="What Flock Bot has cost across all churches this month, and the caps that stop it. Months follow Central time."
        refreshing={Boolean(report.refreshing || list.refreshing)}
        onRefresh={() => {
          loadReport();
          loadLimits();
        }}
      />
      {report.status === 'ready' ? <StaleNotice loadedAt={report.loadedAt} /> : null}

      {notice ? (
        <div
          role={notice.tone === 'success' ? 'status' : 'alert'}
          className={`rounded-lg border px-4 py-3 text-sm ${
            notice.tone === 'success'
              ? 'border-green-200 bg-green-50 text-green-800'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {notice.text}
        </div>
      ) : null}

      {report.status === 'failed' ? (
        <LoadFailed title="Flock Bot spend could not be loaded" message={report.message} onRetry={loadReport} />
      ) : (
        <PageState loading={report.status === 'loading'}>
          {data ? (
            <>
              <MonthPicker today={data.today} value={month ?? data.month} onChange={setMonth} />
              {otherMonthShown && report.refreshError ? (
                <LoadFailed
                  title={`Spend for ${formatMonth(month)} could not be loaded`}
                  message={report.refreshError}
                  onRetry={loadReport}
                />
              ) : otherMonthShown ? (
                <PageState loading />
              ) : (
                <>
                  {report.refreshError ? <RefreshFailed what="Spend" message={report.refreshError} /> : null}
                  {summary.isCurrentMonth ? <BreakerNotice level={summary.level} /> : null}
                  {summary.isEmpty ? (
                    <PageState
                      empty
                      emptyTitle={`No Flock Bot spend in ${formatMonth(data.month)}`}
                      emptyMessage={
                        summary.isCurrentMonth
                          ? 'Spend shows here after members ask Flock Bot their first questions this month.'
                          : 'Flock Bot made no paid calls this month.'
                      }
                    />
                  ) : (
                    <>
                      <SpendTiles summary={summary} caps={data.caps} />
                      <SpendChart report={data} />
                      <ChurchTable report={data} />
                    </>
                  )}
                </>
              )}
            </>
          ) : null}
        </PageState>
      )}

      <CapsSection
        list={list}
        onRetry={loadLimits}
        monthSpentMicroUsd={summary?.isCurrentMonth && !otherMonthShown ? summary.spentMicroUsd : undefined}
        onSaved={(nextNotice) => {
          setNotice(nextNotice);
          loadLimits();
          loadReport();
        }}
        onNotice={setNotice}
      />
    </div>
  );
}

function MonthPicker({ today, value, onChange }) {
  const options = monthOptions(today);
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
      <label htmlFor="flockbot-spend-month" className="text-sm font-semibold text-gray-800">
        Month
      </label>
      <select
        id="flockbot-spend-month"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={`min-h-[44px] rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 sm:w-56 ${focusRing}`}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function BreakerNotice({ level }) {
  if (level === 'ok') return null;
  const hard = level === 'hard';
  return (
    <div
      role="status"
      className={`rounded-lg border px-4 py-3 text-sm ${
        hard ? 'border-red-300 bg-red-50 text-red-900' : 'border-amber-300 bg-amber-50 text-amber-950'
      }`}
    >
      <p className="font-semibold">{hard ? 'Flock Bot is paused for every church.' : 'Church lookups are off.'}</p>
      <p className="mt-1">
        {hard
          ? 'This month reached the hard cap. Flock Bot stays paused until the 1st, or until you raise the cap below.'
          : 'This month passed the soft cap. Flock Bot answers from the help guide only until the 1st, or until you raise the cap below.'}
      </p>
    </div>
  );
}

function Tile({ title, value, detail, className = 'border-gray-200 bg-white', children }) {
  return (
    <div className={`rounded-lg border p-4 ${className}`}>
      <p className="text-sm font-semibold text-gray-700">{title}</p>
      <p className="mt-2 text-2xl font-bold tabular-nums text-gray-900">{value}</p>
      {children}
      {detail ? <p className="mt-1 text-xs text-gray-600">{detail}</p> : null}
    </div>
  );
}

function SpendTiles({ summary, caps }) {
  const width = barWidthPercent(summary.percentOfHard ?? 100);
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <Tile
        title={summary.isCurrentMonth ? 'Spent this month' : 'Spent'}
        value={formatMicroDollars(summary.spentMicroUsd)}
        className={LEVEL_TILE_CLASSES[summary.level]}
        detail={`Lookups turn off past ${formatMicroDollars(caps.softMonthlyMicroUsd)}.${
          summary.reservedMicroUsd > 0 ? ` Includes ${formatMicroDollars(summary.reservedMicroUsd)} in flight.` : ''
        }`}
      >
        <p className="mt-1 text-sm text-gray-700">
          of {formatMicroDollars(caps.hardMonthlyMicroUsd)} ({formatPercent(summary.percentOfHard)})
        </p>
        <div
          role="progressbar"
          aria-label="Spend toward the monthly hard cap"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={width}
          aria-valuetext={formatPercent(summary.percentOfHard)}
          className="mt-2 h-2 overflow-hidden rounded-full bg-gray-200"
        >
          <div className={`h-full rounded-full ${LEVEL_BAR_CLASSES[summary.level]}`} style={{ width: `${width}%` }} />
        </div>
      </Tile>
      {summary.isCurrentMonth ? (
        <>
          <Tile
            title="Projected month-end"
            value={formatMicroDollars(summary.projectedMicroUsd)}
            detail="If the rest of the month keeps this month's pace."
          />
          <Tile title="Today" value={formatMicroDollars(summary.todayMicroUsd)} detail="Since midnight Central time." />
        </>
      ) : (
        <>
          <Tile title="Month total" value={formatMicroDollars(summary.projectedMicroUsd)} detail="Settled spend." />
          <Tile title="Daily average" value={formatMicroDollars(summary.dailyAverageMicroUsd)} />
        </>
      )}
      <Tile title={summary.isCurrentMonth ? 'Calls this month' : 'Calls'} value={formatCount(summary.calls)} />
    </div>
  );
}

function SpendChart({ report }) {
  const rows = chartRows(report.days);
  const soft = microToDollars(report.caps.softMonthlyMicroUsd);
  const hard = microToDollars(report.caps.hardMonthlyMicroUsd);
  const lastDay = report.days[report.days.length - 1]?.date;

  return (
    <section
      aria-labelledby="flockbot-spend-chart-title"
      className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
    >
      <h4 id="flockbot-spend-chart-title" className="font-semibold text-gray-900">
        Spend by day, {formatMonth(report.month)}
      </h4>
      <p className="mt-1 text-sm text-gray-600">
        Bars are each day&apos;s spend (right axis). The line is the month&apos;s running total against the soft and
        hard caps (left axis).{lastDay ? ` Through ${formatLongDate(lastDay)}.` : ''}
      </p>
      <div className="mt-3 h-80">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 16, right: 8, left: 8, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
            <XAxis dataKey="label" interval="preserveStartEnd" minTickGap={12} tick={AXIS_TICK} />
            <YAxis
              yAxisId="total"
              tick={AXIS_TICK}
              tickFormatter={formatDollars}
              width={72}
              domain={[0, (dataMax) => Math.max(dataMax, hard, soft) * 1.1]}
            />
            <YAxis yAxisId="daily" orientation="right" tick={AXIS_TICK} tickFormatter={formatDollars} width={64} />
            <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(value, name) => [formatDollars(value), name]} />
            <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }} />
            <ReferenceLine
              yAxisId="total"
              y={soft}
              stroke={SOFT_COLOR}
              strokeDasharray="4 4"
              label={{
                value: `Soft cap ${formatDollars(soft)}`,
                position: 'insideTopLeft',
                fill: '#92400e',
                fontSize: 11,
              }}
            />
            <ReferenceLine
              yAxisId="total"
              y={hard}
              stroke={HARD_COLOR}
              strokeDasharray="4 4"
              label={{
                value: `Hard cap ${formatDollars(hard)}`,
                position: 'insideTopLeft',
                fill: '#991b1b',
                fontSize: 11,
              }}
            />
            <Bar
              yAxisId="daily"
              dataKey="dailyDollars"
              name="Spend that day"
              fill="#93c5fd"
              isAnimationActive={false}
            />
            <Line
              yAxisId="total"
              type="monotone"
              dataKey="cumulativeDollars"
              name="Month to date"
              stroke="#2563eb"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

function ChurchTable({ report }) {
  const total = report.totals.spentMicroUsd;
  return (
    <section aria-labelledby="flockbot-spend-churches-title" className="space-y-3">
      <div>
        <h4 id="flockbot-spend-churches-title" className="font-semibold text-gray-900">
          Churches
        </h4>
        <p className="mt-1 text-sm text-gray-600">
          The 25 churches that spent the most in {formatMonth(report.month)}. Each church can spend up to{' '}
          {formatMicroDollars(report.caps.orgDailyMicroUsd)} a day.
        </p>
      </div>
      {report.churches.length === 0 ? (
        <p className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-600">
          No church has settled spend this month yet.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <caption className="sr-only">Flock Bot spend by church</caption>
            <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              <tr>
                <th scope="col" className="px-4 py-3">
                  Church
                </th>
                <th scope="col" className="px-4 py-3 text-right">
                  This month
                </th>
                <th scope="col" className="px-4 py-3 text-right">
                  Today
                </th>
                <th scope="col" className="px-4 py-3 text-right">
                  Calls
                </th>
                <th scope="col" className="px-4 py-3 text-right">
                  Share
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {report.churches.map((church) => (
                <tr key={church.organizationId}>
                  <th scope="row" className="px-4 py-3 text-left font-medium text-gray-900">
                    {church.name || 'Unnamed church'}
                  </th>
                  <td className="px-4 py-3 text-right tabular-nums text-gray-900">
                    {formatMicroDollars(church.spentMicroUsd)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-gray-900">
                    {formatMicroDollars(church.todaySpentMicroUsd)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-gray-900">{formatCount(church.calls)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-gray-900">
                    {formatPercent(sharePercent(church.spentMicroUsd, total))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function SourceBadge({ source }) {
  return (
    <span
      className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${
        SOURCE_BADGE_CLASSES[source] ?? SOURCE_BADGE_CLASSES.default
      }`}
    >
      {sourceLabel(source)}
    </span>
  );
}

function CapsSection({ list, onRetry, monthSpentMicroUsd, onSaved, onNotice }) {
  const [editor, setEditor] = useState(null);

  if (list.status === 'loading') {
    return (
      <section aria-label="Caps">
        <PageState loading />
      </section>
    );
  }
  if (list.status === 'failed') {
    return <LoadFailed title="Caps could not be loaded" message={list.message} onRetry={onRetry} />;
  }
  if (list.status !== 'ready') return null;

  const caps = capLimits(list.payload.limits ?? []);

  function openEditor(limit) {
    onNotice(null);
    setEditor({
      base: limit,
      target: 'value',
      text: dollarsInputText(limit.value),
      reason: '',
      acknowledged: false,
      status: { phase: 'editing', message: null },
    });
  }

  function editDraft(changes) {
    setEditor((current) => ({
      ...current,
      ...changes,
      status: current.status.phase === 'conflict' ? current.status : { phase: 'editing', message: null },
    }));
  }

  function rebaseOnCurrent() {
    setEditor((current) => ({
      ...current,
      base: current.status.current,
      target: 'value',
      text: dollarsInputText(current.status.current.value),
      acknowledged: false,
      status: { phase: 'editing', message: null },
    }));
  }

  function closeWith(nextNotice) {
    setEditor(null);
    onSaved(nextNotice);
  }

  function alreadySaved(current) {
    return { tone: 'success', text: `${current.label} is already ${describeCap(current)}. No retry is needed.` };
  }

  async function save() {
    const plan = planCapChange(editor.base, editor, monthSpentMicroUsd);
    if (!plan.ok) return;
    const { base } = editor;
    const body = buildUpdateBody(editor, plan);
    setEditor((current) => ({ ...current, status: { phase: 'saving' } }));

    try {
      const response = await runtimeLimitsAPI.update(base.key, body);
      const saved = response.data.limit;
      closeWith({
        tone: 'success',
        text: response.data.unchanged
          ? `${saved.label} was already ${describeCap(saved)}. Nothing changed.`
          : `${saved.label} changed from ${describeCap(base)} to ${describeCap(saved)}.`,
      });
    } catch (error) {
      const failure = readRequestError(error, 'The cap was not changed.');

      if (failure.type === 'conflict') {
        if (outcomeHolds(failure.current, body.value)) {
          closeWith(alreadySaved(failure.current));
        } else {
          setEditor((current) => ({ ...current, status: { phase: 'conflict', current: failure.current } }));
          onRetry();
        }
        return;
      }

      if (failure.type === 'denied') {
        closeWith({ tone: 'error', text: failure.message });
        return;
      }

      if (failure.type === 'no_response') {
        setEditor((current) => ({ ...current, status: { phase: 'checking' } }));
        const fresh = await onRetry();
        const current = fresh?.limits?.find((limit) => limit.key === base.key);
        if (current && outcomeHolds(current, body.value)) {
          closeWith(alreadySaved(current));
          return;
        }
        setEditor((draft) => ({
          ...draft,
          status: {
            phase: 'editing',
            message: current
              ? 'The connection dropped before the server answered. The latest values show this change did not save. You can try again.'
              : 'The connection dropped before the server answered, and the caps could not be re-read. Check your connection, then try again.',
          },
        }));
        return;
      }

      setEditor((current) => ({ ...current, status: { phase: 'editing', message: failure.message } }));
    }
  }

  return (
    <section
      aria-labelledby="flockbot-caps-title"
      className="space-y-3 rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
    >
      <div>
        <h4 id="flockbot-caps-title" className="font-semibold text-gray-900">
          Caps
        </h4>
        <p className="mt-1 text-sm text-gray-600">
          Saved caps apply everywhere in Flock on the next question, with no redeploy. Monthly caps reset on the 1st and
          daily caps at midnight, Central time.
        </p>
      </div>
      {list.refreshError ? <RefreshFailed what="Caps" message={list.refreshError} /> : null}
      {caps.length === 0 ? (
        <p className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-600">
          The server reported no Flock Bot caps.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
          {caps.map((limit) => (
            <CapRow key={limit.key} limit={limit} onChange={() => openEditor(limit)} />
          ))}
        </ul>
      )}
      {editor ? (
        <CapEditor
          editor={editor}
          monthSpentMicroUsd={monthSpentMicroUsd}
          onEdit={editDraft}
          onRebase={rebaseOnCurrent}
          onSave={save}
          onClose={() => setEditor(null)}
        />
      ) : null}
    </section>
  );
}

function CapRow({ limit, onChange }) {
  return (
    <li className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-medium text-gray-900">{limit.label}</p>
          <SourceBadge source={limit.source} />
        </div>
        <p className="mt-1 text-2xl font-bold tabular-nums text-gray-900">
          {formatMicroDollars(limit.value)}
          {limit.value === 0 ? <span className="ml-2 text-sm font-semibold text-amber-800">Off</span> : null}
        </p>
        <p className="mt-1 text-sm text-gray-600">{limit.description}</p>
        {isPinned(limit) ? (
          <p className="mt-1 text-xs text-gray-600">
            {describeChanger(limit)} Without it, this cap is {formatMicroDollars(limit.fallbackValue)} (
            {sourceLabel(limit.fallbackSource)}).
          </p>
        ) : null}
      </div>
      <button
        type="button"
        onClick={onChange}
        className={`inline-flex min-h-[44px] flex-none items-center gap-2 self-start rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 ${focusRing}`}
      >
        <PencilSquareIcon className="h-4 w-4" />
        Change<span className="sr-only"> {limit.label}</span>
      </button>
    </li>
  );
}

function acknowledgementText(base, plan) {
  if (plan.change === 'revert') {
    return `Yes, stop using this Fold value and go back to ${formatMicroDollars(plan.next)}.`;
  }
  if (plan.change === 'pin') {
    const current = base.source === 'default' ? 'the default' : 'the Railway setting';
    return `Yes, use ${formatMicroDollars(plan.next)} everywhere in Flock instead of ${current} (${formatMicroDollars(base.value)}).`;
  }
  return `Yes, raise this from ${formatMicroDollars(base.value)} to ${formatMicroDollars(plan.next)}.`;
}

function CapEditor({ editor, monthSpentMicroUsd, onEdit, onRebase, onSave, onClose }) {
  const { base, status } = editor;
  const plan = planCapChange(base, editor, monthSpentMicroUsd);
  const busy = status.phase === 'saving' || status.phase === 'checking';
  const reverting = editor.target === 'revert';
  const showInvalid = !reverting && editor.text !== '' && !plan.ok;

  return (
    <Modal
      title={`Change ${base.label}`}
      description={base.description}
      confirmLabel={reverting ? 'Revert' : 'Save'}
      confirmDisabled={
        !plan.ok ||
        plan.change === 'unchanged' ||
        (plan.needsAcknowledgement && !editor.acknowledged) ||
        status.phase === 'conflict'
      }
      busy={busy}
      onConfirm={onSave}
      onClose={onClose}
    >
      {status.phase === 'conflict' ? (
        <div role="alert" className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
          <p className="font-semibold">This cap changed while you were editing. Your draft was not saved.</p>
          <p className="mt-1">
            Current saved value: {describeCap(status.current)}.{' '}
            {isPinned(status.current) ? describeChanger(status.current) : null}
          </p>
          <button
            type="button"
            onClick={onRebase}
            className={`mt-3 min-h-[44px] rounded-lg border border-amber-400 bg-white px-3 py-2 text-sm font-semibold text-amber-950 hover:bg-amber-100 ${focusRing}`}
          >
            Use current value
          </button>
        </div>
      ) : null}
      {status.phase === 'checking' ? (
        <div
          role="status"
          className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900"
        >
          The connection dropped before the server answered. Checking whether the change saved…
        </div>
      ) : null}
      {status.phase === 'editing' && status.message ? (
        <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {status.message}
        </div>
      ) : null}

      {reverting ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800">
          <p>
            Saving goes back to the {sourceLabel(base.fallbackSource).toLowerCase()} value:{' '}
            {formatMicroDollars(base.fallbackValue)}.
          </p>
          <button
            type="button"
            onClick={() => onEdit({ target: 'value', acknowledged: false })}
            disabled={busy}
            className={`mt-3 min-h-[44px] rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-60 ${focusRing}`}
          >
            Set a value instead
          </button>
        </div>
      ) : (
        <>
          <label htmlFor="flockbot-cap-value" className="block text-sm font-semibold text-gray-800">
            New cap (dollars)
          </label>
          <div className="relative mt-2">
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-gray-500"
            >
              $
            </span>
            <input
              id="flockbot-cap-value"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={editor.text}
              disabled={busy}
              aria-invalid={showInvalid}
              aria-describedby={`flockbot-cap-value-help${showInvalid ? ' flockbot-cap-value-error' : ''}`}
              onChange={(event) => onEdit({ text: event.target.value, acknowledged: false })}
              className={`min-h-[44px] w-full rounded-lg border border-gray-300 py-2 pl-7 pr-3 text-base tabular-nums text-gray-900 ${focusRing}`}
            />
          </div>
          <p id="flockbot-cap-value-help" className="mt-2 text-xs text-gray-600">
            {capRangeMessage(base)} Current value: {describeCap(base)}.
          </p>
          {showInvalid ? (
            <p id="flockbot-cap-value-error" aria-live="polite" className="mt-2 text-sm font-medium text-red-700">
              {plan.message}
            </p>
          ) : null}
          {isPinned(base) ? (
            <button
              type="button"
              onClick={() => onEdit({ target: 'revert', acknowledged: false })}
              disabled={busy}
              className={`mt-3 inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60 ${focusRing}`}
            >
              <ArrowUturnLeftIcon className="h-4 w-4" />
              Revert to {sourceLabel(base.fallbackSource).toLowerCase()} ({formatMicroDollars(base.fallbackValue)})
            </button>
          ) : null}
        </>
      )}

      {plan.ok && plan.change === 'unchanged' ? (
        <p className="mt-3 text-sm text-gray-600">This is already the saved value.</p>
      ) : null}
      {plan.ok && plan.warning ? (
        <p
          aria-live="polite"
          className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950"
        >
          {plan.warning}
        </p>
      ) : null}
      {plan.ok && plan.needsAcknowledgement ? (
        <label className="mt-4 flex min-h-[44px] cursor-pointer items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">
          <input
            type="checkbox"
            required
            checked={editor.acknowledged}
            disabled={busy}
            onChange={(event) => onEdit({ acknowledged: event.target.checked })}
            className={`mt-0.5 h-5 w-5 flex-none ${focusRing}`}
          />
          <span>{acknowledgementText(base, plan)}</span>
        </label>
      ) : null}

      <label htmlFor="flockbot-cap-reason" className="mt-4 block text-sm font-semibold text-gray-800">
        Reason (optional)
      </label>
      <textarea
        id="flockbot-cap-reason"
        rows={2}
        maxLength={REASON_MAX_LENGTH}
        value={editor.reason}
        disabled={busy}
        aria-describedby="flockbot-cap-reason-count"
        onChange={(event) => onEdit({ reason: event.target.value })}
        className={`mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 ${focusRing}`}
      />
      <p id="flockbot-cap-reason-count" className="mt-1 text-xs text-gray-500">
        {editor.reason.length}/{REASON_MAX_LENGTH} characters. Saved with the audit record.
      </p>
    </Modal>
  );
}
